import React, { useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Stack from '@mui/material/Stack';
import Button from 'components/Button';
import NameQuestion from './NameQuestion';
import SchedulerQuestion from './SchedulerQuestion';
import GuestsQuestion from './GuestsQuestion';
import AddressQuestion from './AddressQuestion';
import MultipleChoiceQuestion from './MultipleChoiceQuestion';
import EmailQuestion from './EmailQuestion';
import BooleanQuestion from './BooleanQuestion';
import DateQuestion from './DateQuestion';
import ShortTextQuestion from './ShortTextQuestion';
import LongTextQuestion from './LongTextQuestion';
import NumberQuestion from './NumberQuestion';
import PhoneNumberQuestion from './PhoneNumberQuestion';
import SurveyQuestion from './SurveyQuestion';
import RatingQuestion from './RatingQuestion';
import BasicOptions from './BasicOptions';
import times from 'lodash/times';

export const QuestionComponents = {
  name: NameQuestion,
  scheduler: SchedulerQuestion,
  add_member: GuestsQuestion,
  address: AddressQuestion,
  choice: MultipleChoiceQuestion,
  email: EmailQuestion,
  boolean: BooleanQuestion,
  date: DateQuestion,
  text: ShortTextQuestion,
  paragraph: LongTextQuestion,
  number: NumberQuestion,
  phone: PhoneNumberQuestion,
  survey: SurveyQuestion,
  rating: RatingQuestion,
};

const ConfigurationStep = ({ question, values, onChange }) => {
  return (
    <BasicOptions question={question} values={values} onChange={onChange} />
  );
};

const PreviewStep = ({ Component, question }) => {
  return (
    <>
      <Typography paragraph fontWeight={500}>
        Review & Confirm
      </Typography>
      <Card elevation={1} sx={{ p: 3 }}>
        <Component question={question} />
      </Card>
    </>
  );
};

const configureStepDescription = 'How would you like to phrase your question?';
const previewStepDescription =
  'Take a look at the preview. Does everything look correct?';

const ConfigureQuestionModal = ({ onClose, question, onQuestionUpdate }) => {
  const [t] = useTranslation('connectCard');
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = React.useState({});
  const [values, setValues] = useState({
    ...question,
  });

  const Component = QuestionComponents[question.question_type];
  const totalSteps = Component.StepsCount;
  const steps = times(totalSteps);

  const completedSteps = () => {
    return Object.keys(completed).length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps - 1;
  };

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps;
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  const handleNext = () => {
    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? // It's the last step, but not all steps have been completed,
          // find the first step that has been completed
          steps.findIndex((step, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onValuesChange = (v) => {
    setValues((prev) => ({ ...prev, ...v }));
  };

  const handleSave = async () => {
    const update = {
      ...question,
      ...values,
    };
    update.question_settings = JSON.stringify(update.question_settings);
    await onQuestionUpdate(update);
    onClose();
  };

  const stepTitle = useMemo(() => {
    if (activeStep === 0) {
      return 'Ask your question';
    }
    if (isLastStep()) {
      return 'Preview your question';
    }
    return 'Ask your question (Part B)';
  }, [activeStep]);
  const description = useMemo(() => {
    if (activeStep === 0) {
      return configureStepDescription;
    }
    if (isLastStep()) {
      return previewStepDescription;
    }
    return Component.StepDescription;
  }, [activeStep]);

  const isValid = useMemo(() => {
    if (activeStep === 0) {
      return !!values.question_label;
    }
    if (isLastStep()) {
      return true;
    }
    switch (question.question_type) {
      case 'choice': {
        return !!values.question_possible_answers?.filter((a) => a.value)
          .length;
      }
      case 'scheduler': {
        return values.question_possible_answers?.filter((i) => {
          return i.date || (i.dow && i.time);
        }).length;
      }
      default:
        return true;
    }
  }, [activeStep, values]);

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: 650,
        },
      }}
    >
      <DialogTitle>
        <Stack>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="h3">{stepTitle}</Typography>
            <Stepper activeStep={activeStep} sx={{ width: 200 }}>
              {times(Component.StepsCount).map((label, index) => (
                <Step key={label} completed={completed[index]}>
                  <StepButton color="inherit" onClick={handleStep(index)} />
                </Step>
              ))}
            </Stepper>
          </Stack>
          <Typography color="text.secondary">{description}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {activeStep === 0 && (
          <ConfigurationStep values={values} onChange={onValuesChange} />
        )}
        {activeStep === 1 && !isLastStep() && (
          <Component values={values} onChange={onValuesChange} />
        )}
        {isLastStep() && (
          <PreviewStep question={values} Component={Component.Preview} />
        )}
      </DialogContent>
      <DialogActions>
        {activeStep === 0 ? (
          <Button variant="text" onClick={onClose}>
            {t('common:cancel', 'Cancel')}
          </Button>
        ) : (
          <Button variant="text" onClick={handleBack}>
            {t('common:back', 'Back')}
          </Button>
        )}
        {isLastStep() ? (
          <Button onClick={handleSave}> {t('common:save', 'Save')}</Button>
        ) : (
          <Button disabled={!isValid} onClick={handleNext}>
            {t('common:next', 'Next')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConfigureQuestionModal;
